"use client";
import LocalizedLink from "@/components/LocalizedLink";
import {
  Field,
  Input,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ArrowLeftIcon } from "@heroicons/react/16/solid";
import { ChevronUpDownIcon, PhoneIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { z } from "zod";

type FormFields = {
  fields_name: string;
  fields_surname: string;
  fields_email: string;
  fields_phone: string;
  fields_city: string;
  fields_age: string;
  fields_job: string;
  fields_source: string;
};

// Схема валідації
const formSchema = z.object({
  fields_name: z
    .string({
      required_error: "Ім'я є обов'язковим",
    })
    .min(1, "Ім'я є обов'язковим"),
  fields_surname: z.string().min(1, "Прізвище є обов'язковим"),
  fields_phone: z
    .string({
      required_error: "Телефон є обов'язковим",
    })
    .min(1, "Телефон є обов'язковим")
    .regex(/^\+?[0-9]{10,14}$/, "Невірний формат номера телефону"),
  fields_city: z
    .string({
      required_error: "Місто є обов'язковим",
    })
    .min(1, "Місто є обов'язковим"),
  fields_source: z
    .string({
      required_error: "Вкажіть звідки ви дізнались про конференцію",
    })
    .refine((val) => val.length > 0, {
      message: "Вкажіть звідки саме ви дізнались про конференцію",
    }),
  fields_role: z.string({
    required_error: "Вкажіть вашу роль на конференції",
  }),
  fields_age: z.coerce
    .number({
      invalid_type_error: "Вік має бути числом",
      required_error: "Вік є обов'язковим",
    })
    .min(1, "Вік є обов'язковим"),
  fields_job: z
    .string({
      required_error: "Вкажіть де ви працюєте",
    })
    .min(1, "Вкажіть де ви працюєте"),
  fields_email: z
    .string({
      required_error: "Вкажіть вашу електронну пошту",
    })
    .trim()
    .email({
      message: "Невірний формат електронної пошти",
    }),
});

export default function ConferenceForm() {
  const backgroundColorClass = "custom-gradient-green-btn";
  const router = useRouter();
  const [formData, setFormData] = useState<FormFields>({
    fields_name: "",
    fields_surname: "",
    fields_phone: "",
    fields_city: "",
    fields_age: "",
    fields_job: "",
    fields_email: "",
    fields_source: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<FormFields>({
    fields_name: "",
    fields_surname: "",
    fields_phone: "",
    fields_city: "",
    fields_age: "",
    fields_job: "",
    fields_email: "",
    fields_source: "",
  });
  const [source, setSource] = useState<string>("");
  const [role, setRole] = useState<string>("Учасник");
  const [customSource, setCustomSource] = useState("");
  const [isInputVisible, setInputVisible] = useState(false);

  const link = "https://ap-server-8qi1.onrender.com/leads/conference";
  const sourceOptions = [
    "Соцмережі",
    "Рекомендації",
    "Афіша",
    "Запрошення",
    "Інше",
  ];
  const roleOptions = ["Учасник", "Спікер"];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (formErrors && formErrors[name as keyof FormFields]) {
      setFormErrors((prevData) => ({
        ...prevData,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);
    setLoading(true);
    setError(null);
    setFormErrors({
      fields_name: "",
      fields_surname: "",
      fields_phone: "",
      fields_city: "",
      fields_age: "",
      fields_job: "",
      fields_email: "",
      fields_source: "",
    });
    const utmTags = JSON.parse(localStorage.getItem("utmTags") || "{}") || {};
    const data = {
      fields_name: formData.fields_name,
      fields_surname: formData.fields_surname,
      fields_phone: formData.fields_phone,
      fields_city: formData.fields_city,
      fields_age: formData.fields_age,
      fields_source: source === "Інше" ? customSource : source,
      fields_role: role,
      fields_job: formData.fields_job,
      fields_email: formData.fields_email,
    };

    try {
      const validatedData = formSchema.parse(data);
      await axios.post(link, {
        name: `${validatedData.fields_name} ${validatedData.fields_surname}`,
        phone: validatedData.fields_phone,
        city: validatedData.fields_city,
        age: validatedData.fields_age,
        source: validatedData.fields_source,
        role: validatedData.fields_role,
        job: validatedData.fields_job,
        email: validatedData.fields_email,
        tag: "конференція",
        ...utmTags,
      });

      setSuccess(true);
      router.push("/thankyou");
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: FormFields = {
          fields_name: "",
          fields_surname: "",
          fields_phone: "",
          fields_city: "",
          fields_age: "",
          fields_job: "",
          fields_email: "",
          fields_source: "",
        };

        error.issues.forEach((err) => {
          const fieldName = err.path[0] as keyof FormFields;
          newErrors[fieldName] = err.message;
        });
        setFormErrors(newErrors);
        setError(
          newErrors.fields_name ||
            newErrors.fields_surname ||
            newErrors.fields_phone ||
            newErrors.fields_city ||
            newErrors.fields_age ||
            newErrors.fields_email ||
            newErrors.fields_job ||
            newErrors.fields_source,
        );
      } else {
        setError("Форма не відправлена. Спробуйте, будь ласка, ще раз.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSourceChange = (value: string) => {
    setSource(value);
    if (formErrors && formErrors.fields_source) {
      setFormErrors((prevData) => ({
        ...prevData,
        fields_source: "",
      }));
    }
    if (value === "Інше") {
      setInputVisible(true);
    } else {
      setInputVisible(false);
      setCustomSource(""); // Clear the custom source input when a different option is selected
    }
  };

  return (
    <div className="w-full flex flex-col items-center justify-center relative">
      <header>
        <nav
          aria-label="Global"
          className="relative mx-auto flex items-center lg:gap-4 justify-between p-6 lg:px-2 xl:px-8 z-50"
        >
          <div className="flex">
            <LocalizedLink href={"/"} className="-m-1.5 p-1.5">
              <span className="sr-only">App Education</span>
              <Image
                width={100}
                height={38}
                alt=""
                src={"/assets/icon/LogoRevers.svg"}
                className="h-8 w-32 hidden lg:block"
              />
              <Image
                width={100}
                height={38}
                alt=""
                src={"/assets/icon/AP.svg"}
                className="h-8 w-16 lg:hidden"
              />
            </LocalizedLink>
          </div>
          <div className="flex justify-end items-center gap-x-8">
            <a
              href="tel:+380936707129"
              className="flex gap-x-2 items-center text-xs xl:text-sm font-semibold leading-6 text-white"
            >
              <PhoneIcon className="h-[20px] w-[20px] text-white" />
              +38 (093) 670 71 29
            </a>
          </div>
        </nav>
      </header>

      <Image
        className="absolute inset-0 w-full object-cover -z-50 h-screen"
        src={"/assets/photoAP/conferenceBg.webp"}
        width={1920}
        height={1080}
        title="AP Education"
        alt=""
      ></Image>
      <div className="grid grid-cols-1 lg:grid-cols-12 max-w-7xl mx-auto rounded-3xl mt-8">
        <div className="relative flex flex-col justify-center items-center px-6 pb-8 max-lg:mt-8 lg:px-8 lg:col-span-full">
          <div className="flex flex-col justify-between mx-auto lg:pl-8 lg:max-w-2xl lg:mx-0 max-w-lg">
            <LocalizedLink
              href={"/"}
              className="text-white mb-6 flex justify-center items-baseline"
            >
              <div className="flex justify-center items-center">
                <div className="border border-white rounded-full p-2 mr-4">
                  <ArrowLeftIcon className="h-full w-5 " />
                </div>
                <span className="text-sm lg:text-xl">
                  Повернутися на головну сторінку
                </span>
              </div>
            </LocalizedLink>
            <p className="text-3xl flex-1 lg:text-5xl font-bold tracking-tight text-white text-center">
              Ця подія вже відбулася, дякуємо за увагу
            </p>
            <div className="hidden lg:flex justify-center items-center flex-col mt-12">
              <p className="text-white text-lg mb-4">
               Підпишіться на нас, щоб не пропустити наступні:
              </p>
              <div className="flex gap-3">
                <div
                  className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
                >
                  <Link
                    href={"https://www.facebook.com/ap.edu.centre/"}
                    target="_blank"
                  >
                    <Image
                      width={64}
                      height={64}
                      className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                      src="/assets/icon/FBSolid.svg"
                      alt=""
                    />
                  </Link>
                </div>
                <div
                  className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
                >
                  <Link
                    href={
                      "https://www.tiktok.com/@ap.education.center?_t=8qLAl2a7AUH&_r=1"
                    }
                    target="_blank"
                  >
                    <Image
                      width={64}
                      height={64}
                      className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                      src="/assets/icon/TKSolid.svg"
                      alt=""
                    />
                  </Link>
                </div>
                <div
                  className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
                >
                  <Link
                    href={"https://www.instagram.com/ap.education"}
                    target="_blank"
                  >
                    <Image
                      width={64}
                      height={64}
                      className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                      src="/assets/icon/InstSolid.svg"
                      alt=""
                    />
                  </Link>
                </div>
                <div
                  className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
                >
                  <Link
                    href={
                      "https://www.linkedin.com/company/ap-education-center"
                    }
                    target="_blank"
                  >
                    <Image
                      width={64}
                      height={64}
                      className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                      src="/assets/icon/LinkSolid.svg"
                      alt=""
                    />
                  </Link>
                </div>
                <div
                  className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
                >
                  <Link
                    href={"https://www.youtube.com/@ap.education.center"}
                    target="_blank"
                  >
                    <Image
                      width={64}
                      height={64}
                      className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                      src="/assets/icon/YTSolid.svg"
                      alt=""
                    />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/*<form*/}
        {/*  autoComplete="off"*/}
        {/*  onSubmit={handleSubmit}*/}
        {/*  className="px-4 lg:col-start-7 lg:col-span-6"*/}
        {/*>*/}
        {/*  <div className="mx-auto lg:max-w-2xl max-w-lg lg:mr-0 rounded-3xl bg-white">*/}
        {/*    <div className="p-4">*/}
        {/*      <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">*/}
        {/*        <div>*/}
        {/*          <Field>*/}
        {/*            <Label className="ml-2 text-[0.7rem] text-gray-600">*/}
        {/*              Ім'я**/}
        {/*            </Label>*/}
        {/*            <Input*/}
        {/*              id="fields_name"*/}
        {/*              autoComplete="off"*/}
        {/*              name="fields_name"*/}
        {/*              type="text"*/}
        {/*              value={formData.fields_name}*/}
        {/*              onChange={handleInputChange}*/}
        {/*              className={clsx(*/}
        {/*                "block w-full rounded-full border-none ring-1 ring-inset ring-green-300",*/}
        {/*                "focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6",*/}
        {/*                formErrors && formErrors?.fields_name*/}
        {/*                  ? "ring-red-400"*/}
        {/*                  : "",*/}
        {/*              )}*/}
        {/*            />*/}
        {/*          </Field>*/}
        {/*        </div>*/}
        {/*        <div>*/}
        {/*          <Field>*/}
        {/*            <Label className="ml-2 text-[0.7rem] text-gray-600">*/}
        {/*              Прізвище**/}
        {/*            </Label>*/}
        {/*            <Input*/}
        {/*              id="fields_surname"*/}
        {/*              autoComplete="off"*/}
        {/*              name="fields_surname"*/}
        {/*              type="text"*/}
        {/*              value={formData.fields_surname}*/}
        {/*              onChange={handleInputChange}*/}
        {/*              className={clsx(*/}
        {/*                "block w-full rounded-full border-none ring-1 ring-inset ring-green-300",*/}
        {/*                "focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6",*/}
        {/*                formErrors && formErrors?.fields_surname*/}
        {/*                  ? "ring-red-400"*/}
        {/*                  : "",*/}
        {/*              )}*/}
        {/*            />*/}
        {/*          </Field>*/}
        {/*        </div>*/}
        {/*        <div>*/}
        {/*          <Field>*/}
        {/*            <Label className="ml-2 text-[0.7rem] text-gray-600">*/}
        {/*              Телефон**/}
        {/*            </Label>*/}
        {/*            <Input*/}
        {/*              id="fields_phone"*/}
        {/*              autoComplete="off"*/}
        {/*              name="fields_phone"*/}
        {/*              type="text"*/}
        {/*              value={formData.fields_phone}*/}
        {/*              onChange={handleInputChange}*/}
        {/*              className={clsx(*/}
        {/*                "block w-full rounded-full border-none ring-1 ring-inset ring-green-300",*/}
        {/*                "focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6",*/}
        {/*                formErrors && formErrors?.fields_phone*/}
        {/*                  ? "ring-red-400"*/}
        {/*                  : "",*/}
        {/*              )}*/}
        {/*            />*/}
        {/*          </Field>*/}
        {/*        </div>*/}
        {/*        <div>*/}
        {/*          <Field>*/}
        {/*            <Label className="ml-2 text-[0.7rem] text-gray-600">*/}
        {/*              Вік**/}
        {/*            </Label>*/}
        {/*            <Input*/}
        {/*              id="fields_age"*/}
        {/*              autoComplete="off"*/}
        {/*              name="fields_age"*/}
        {/*              type="text"*/}
        {/*              value={formData.fields_age}*/}
        {/*              onChange={handleInputChange}*/}
        {/*              className={clsx(*/}
        {/*                "block w-full rounded-full border-none ring-1 ring-inset ring-green-300",*/}
        {/*                "focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6",*/}
        {/*                formErrors && formErrors?.fields_age*/}
        {/*                  ? "ring-red-400"*/}
        {/*                  : "",*/}
        {/*              )}*/}
        {/*            />*/}
        {/*          </Field>*/}
        {/*        </div>*/}
        {/*        <div>*/}
        {/*          <Field>*/}
        {/*            <Label className="ml-2 text-[0.7rem] text-gray-600">*/}
        {/*              Електронна пошта**/}
        {/*            </Label>*/}
        {/*            <Input*/}
        {/*              id="fields_email"*/}
        {/*              name="fields_email"*/}
        {/*              type="text"*/}
        {/*              value={formData.fields_email}*/}
        {/*              onChange={handleInputChange}*/}
        {/*              className={clsx(*/}
        {/*                "block w-full rounded-full border-none ring-1 ring-inset ring-green-300",*/}
        {/*                "focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6",*/}
        {/*                formErrors && formErrors?.fields_email*/}
        {/*                  ? "ring-red-400"*/}
        {/*                  : "",*/}
        {/*              )}*/}
        {/*            />*/}
        {/*          </Field>*/}
        {/*        </div>*/}
        {/*        <div>*/}
        {/*          <Field>*/}
        {/*            <Label className="ml-2 text-[0.7rem] text-gray-600">*/}
        {/*              Місце проживання**/}
        {/*            </Label>*/}
        {/*            <Input*/}
        {/*              id="fields_city"*/}
        {/*              name="fields_city"*/}
        {/*              type="text"*/}
        {/*              value={formData.fields_city}*/}
        {/*              onChange={handleInputChange}*/}
        {/*              className={clsx(*/}
        {/*                "block w-full rounded-full border-none ring-1 ring-inset ring-green-300",*/}
        {/*                "focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6",*/}
        {/*                formErrors && formErrors?.fields_city*/}
        {/*                  ? "ring-red-400"*/}
        {/*                  : "",*/}
        {/*              )}*/}
        {/*            />*/}
        {/*          </Field>*/}
        {/*        </div>*/}
        {/*        <div className="sm:col-span-2 relative">*/}
        {/*          <Listbox value={source} onChange={handleSourceChange}>*/}
        {/*            <label className="ml-2 text-[0.7rem] text-gray-600">*/}
        {/*              Звідки про нас дізналися?**/}
        {/*            </label>*/}
        {/*            <div className="relative mx-auto">*/}
        {/*              <ListboxButton*/}
        {/*                className={clsx(*/}
        {/*                  "relative z-40 w-full cursor-default rounded-full bg-white py-2 p-2 text-left text-gray-600 shadow-sm ring-1 ring-inset ring-green-300 focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6",*/}
        {/*                  formErrors && formErrors?.fields_source*/}
        {/*                    ? "ring-red-400"*/}
        {/*                    : "",*/}
        {/*                )}*/}
        {/*              >*/}
        {/*                <span className="block truncate text-gray-600 ml-2">*/}
        {/*                  {source || "Оберіть варіант"}*/}
        {/*                </span>*/}

        {/*                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">*/}
        {/*                  <ChevronUpDownIcon*/}
        {/*                    aria-hidden="true"*/}
        {/*                    className="h-5 w-5 text-gray-400"*/}
        {/*                  />*/}
        {/*                </span>*/}
        {/*              </ListboxButton>*/}

        {/*              <ListboxOptions className="absolute z-30 -mt-5 pt-6 max-h-60 w-full overflow-auto rounded-md bg-[#F1F1F1] py-1 text-base shadow-lg focus:outline-none sm:text-sm">*/}
        {/*                {sourceOptions.map((option, index) => (*/}
        {/*                  <ListboxOption*/}
        {/*                    key={`source-${index}`}*/}
        {/*                    value={option}*/}
        {/*                    className="group flex justify-between relative cursor-default select-none py-2 pl-8 pr-9 text-gray-500 data-[focus]:bg-green-600 data-[focus]:text-white"*/}
        {/*                  >*/}
        {/*                    <span className="block truncate">{option}</span>*/}
        {/*                  </ListboxOption>*/}
        {/*                ))}*/}
        {/*              </ListboxOptions>*/}
        {/*            </div>*/}
        {/*          </Listbox>*/}

        {/*          {isInputVisible && (*/}
        {/*            <div className="mt-4">*/}
        {/*              <input*/}
        {/*                type="text"*/}
        {/*                value={customSource}*/}
        {/*                onChange={(e) => setCustomSource(e.target.value)}*/}
        {/*                placeholder="Введіть ваш варіант"*/}
        {/*                className={clsx(*/}
        {/*                  "block w-full rounded-full border-none ring-1 ring-inset ring-green-300",*/}
        {/*                  "focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6",*/}
        {/*                  formErrors && formErrors?.fields_source*/}
        {/*                    ? "ring-red-400"*/}
        {/*                    : "",*/}
        {/*                )}*/}
        {/*              />*/}
        {/*            </div>*/}
        {/*          )}*/}
        {/*        </div>*/}
        {/*        <div className="sm:col-span-2">*/}
        {/*          <Listbox value={role} onChange={setRole}>*/}
        {/*            <Label className="ml-2 text-[0.7rem] text-gray-600">*/}
        {/*              На нашій конференції ви..**/}
        {/*            </Label>*/}
        {/*            <div className="relative mx-auto">*/}
        {/*              <ListboxButton className="relative z-20 w-full cursor-default rounded-full bg-white py-2 p-2 text-left text-gray-600 shadow-sm ring-1 ring-inset ring-green-300 focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6">*/}
        {/*                <div className="flex justify-between">*/}
        {/*                  <span className="block truncate text-gray-600 ml-2">*/}
        {/*                    {role}*/}
        {/*                  </span>*/}
        {/*                </div>*/}
        {/*                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">*/}
        {/*                  <ChevronUpDownIcon*/}
        {/*                    aria-hidden="true"*/}
        {/*                    className="h-5 w-5 text-gray-400"*/}
        {/*                  />*/}
        {/*                </span>*/}
        {/*              </ListboxButton>*/}

        {/*              <ListboxOptions*/}
        {/*                transition*/}
        {/*                className="absolute z-10 -mt-5 pt-6 max-h-60 w-full overflow-auto rounded-md bg-[#F1F1F1] py-1 text-base shadow-lg focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"*/}
        {/*              >*/}
        {/*                {roleOptions.map((option, index) => (*/}
        {/*                  <ListboxOption*/}
        {/*                    key={`role-${index}`}*/}
        {/*                    value={option}*/}
        {/*                    className="group flex justify-between relative cursor-default select-none py-2 pl-8 pr-9 text-gray-500 data-[focus]:bg-green-600 data-[focus]:text-white"*/}
        {/*                  >*/}
        {/*                    <span className="block truncate">{option}</span>*/}
        {/*                  </ListboxOption>*/}
        {/*                ))}*/}
        {/*              </ListboxOptions>*/}
        {/*            </div>*/}
        {/*          </Listbox>*/}
        {/*        </div>*/}
        {/*        <div className="sm:col-span-2">*/}
        {/*          <Field>*/}
        {/*            <Label className="ml-2 text-[0.7rem] text-gray-600">*/}
        {/*              Ким працюєте?**/}
        {/*            </Label>*/}
        {/*            <Input*/}
        {/*              id="fields_job"*/}
        {/*              name="fields_job"*/}
        {/*              type="text"*/}
        {/*              value={formData.fields_job}*/}
        {/*              onChange={handleInputChange}*/}
        {/*              className={clsx(*/}
        {/*                "block w-full rounded-full border-none ring-1 ring-inset ring-green-300",*/}
        {/*                "focus:outline-none focus:ring-2 focus:ring-green-400 sm:text-sm sm:leading-6",*/}
        {/*                formErrors && formErrors?.fields_job*/}
        {/*                  ? "ring-red-400"*/}
        {/*                  : "",*/}
        {/*              )}*/}
        {/*            />*/}
        {/*          </Field>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*      <div className="mt-8 flex max-sm:flex-col justify-start">*/}
        {/*        <button*/}
        {/*          type="submit"*/}
        {/*          disabled={loading}*/}
        {/*          className={`${backgroundColorClass} uppercase rounded-full px-8 py-2.5 text-center text-md font-fixel font-bold text-white shadow-sm focus-visible:outline`}*/}
        {/*        >*/}
        {/*          {loading ? "Надсилання..." : "Надіслати"}*/}
        {/*        </button>*/}
        {/*        {error && (*/}
        {/*          <p className="mt-2 ml-2 text-red-500 text-sm">{error}</p>*/}
        {/*        )}*/}
        {/*        {success && (*/}
        {/*          <p className="mt-2 ml-2 text-green-500 text-sm">*/}
        {/*            Форма успішно надіслана!*/}
        {/*          </p>*/}
        {/*        )}*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</form>*/}
        <div className="flex flex-col lg:hidden gap-3 mt-8 mx-auto mb-8">
          <p className="text-white text-center text-lg mb-4">
            Підпишіться на нас, щоб не пропустити наступні:
          </p>
          <div className="flex  gap-3 my-4 mx-auto">
            <div
              className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
            >
              <Link
                href={"https://www.facebook.com/ap.edu.centre/"}
                target="_blank"
              >
                <Image
                  width={64}
                  height={64}
                  className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                  src="/assets/icon/FBSolid.svg"
                  alt=""
                />
              </Link>
            </div>
            <div
              className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
            >
              <Link
                href={
                  "https://www.tiktok.com/@ap.education.center?_t=8qLAl2a7AUH&_r=1"
                }
                target="_blank"
              >
                <Image
                  width={64}
                  height={64}
                  className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                  src="/assets/icon/TKSolid.svg"
                  alt=""
                />
              </Link>
            </div>
            <div
              className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
            >
              <Link
                href={"https://www.instagram.com/ap.education"}
                target="_blank"
              >
                <Image
                  width={64}
                  height={64}
                  className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                  src="/assets/icon/InstSolid.svg"
                  alt=""
                />
              </Link>
            </div>
            <div
              className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
            >
              <Link
                href={"https://www.linkedin.com/company/ap-education-center"}
                target="_blank"
              >
                <Image
                  width={64}
                  height={64}
                  className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                  src="/assets/icon/LinkSolid.svg"
                  alt=""
                />
              </Link>
            </div>
            <div
              className={`h-fit space-y-4 text-base leading-7 text-gray-600 ${backgroundColorClass} rounded-full flex justify-center items-center`}
            >
              <Link
                href={"https://www.youtube.com/@ap.education.center"}
                target="_blank"
              >
                <Image
                  width={64}
                  height={64}
                  className="h-12 w-12 md:h-16 md:w-16 p-2 md:p-4"
                  src="/assets/icon/YTSolid.svg"
                  alt=""
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
